import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useReducer, useEffect, useCallback, useState } from 'react';
import { isEqual } from 'lodash-es';
import { useBiAction } from '../BiTracking';
import { FilterPicker } from '../FilterPicker';
import { Searchbox } from '../Searchbox';
import { CheckboxWithLabel } from './CheckboxWithLabel';
import styles from './MultiSelectFilterPicker.module.scss';
import { createReducer, getInitialSelection } from './MultiSelectFilterPicker.reducer';
export function MultiSelectFilterPicker(props) {
    const { label, options, title, removable, value, withSearchbox = false, ellipsis = false, startOpen = false, selectAllByDefault = true, hideSelectAll = false, selectAllLabel = 'Select all', applyLabel, testId, onChange, onRemove, onInputBlur, onInputFocus, bi, noBi: _noBi } = props;
    const logBiAction = useBiAction();
    const [state, dispatchAction] = useReducer(createReducer(), getInitialSelection(selectAllByDefault, options.length));
    const [search, setSearch] = useState('');
    const trimmedLowercaseSearch = search.trim().toLowerCase();
    const isControlled = typeof value !== 'undefined';
    const getLabel = useCallback((selectedIndexes)=>{
        if (selectedIndexes.length > 1) {
            return "".concat(label, " (").concat(selectedIndexes.length, ")");
        } else if (selectedIndexes.length === 1) {
            return "".concat(label, " (").concat(options[selectedIndexes[0]].label, ")");
        } else {
            return label;
        }
    }, [
        options,
        label
    ]);
    useEffect(()=>{
        if (isControlled) {
            const indexes = [];
            const optionsList = [];
            if (value.length > 0) {
                options.forEach((optionObj, objIndex)=>{
                    if (value.indexOf(optionObj.value) > -1) {
                        optionsList.push(optionObj);
                        indexes.push(objIndex);
                        return true;
                    }
                });
            }
            dispatchAction({
                //todo - don't re-dispatch if already selected
                type: 'selectOptions',
                value: indexes
            });
            dispatchAction({
                type: 'applyChanges',
                label: getLabel(indexes)
            });
        }
    }, [
        value,
        options,
        isControlled,
        getLabel
    ]);
    const allCount = useMemo(()=>{
        return options.reduce((acc, param)=>{
            let { counter } = param;
            return acc + (counter || 0);
        }, 0);
    }, [
        options
    ]);
    return /*#__PURE__*/ _jsx(FilterPicker, {
        startOpen: startOpen,
        testId: testId,
        label: state.labelToDisplay || label,
        highlightTrigger: selectAllByDefault && !state.selectAll,
        title: title || label,
        removable: removable,
        applyLabel: applyLabel,
        headerContent: withSearchbox && /*#__PURE__*/ _jsx(Searchbox, {
            noBi: true,
            className: styles.searchbox,
            testId: testId && "".concat(testId, "-search"),
            value: search,
            onChange: (e)=>setSearch(e.target.value),
            onClear: ()=>setSearch(''),
            onInputFocus: onInputFocus,
            onInputBlur: onInputBlur
        }),
        onRemove: (bi === null || bi === void 0 ? void 0 : bi.name) || onRemove ? ()=>{
            if (bi === null || bi === void 0 ? void 0 : bi.name) {
                logBiAction({
                    entityType: 'multi_select_filter_picker',
                    entityName: bi.name,
                    actionType: 'click',
                    data: _object_spread_props(_object_spread({}, bi.data), {
                        test_id: testId !== null && testId !== void 0 ? testId : ''
                    })
                });
            }
            onRemove === null || onRemove === void 0 ? void 0 : onRemove();
        } : undefined,
        onApply: ()=>{
            if (!isEqual(state.previousState.selectedOptionsIndexes, state.selectedOptionsIndexes)) {
                if (bi === null || bi === void 0 ? void 0 : bi.name) {
                    logBiAction({
                        entityType: 'multi_select_filter_picker',
                        entityName: bi.name,
                        actionType: 'click',
                        data: _object_spread_props(_object_spread({}, bi.data), {
                            test_id: testId !== null && testId !== void 0 ? testId : ''
                        })
                    });
                }
                const selectedOptionsIndexesArray = Array.from(state.selectedOptionsIndexes);
                if (!isControlled) {
                    dispatchAction({
                        type: 'applyChanges',
                        label: getLabel(selectedOptionsIndexesArray)
                    });
                }
                const selectedOptions = selectedOptionsIndexesArray.map((optionIndex)=>options[optionIndex].value);
                onChange(selectedOptions);
            }
        },
        onClose: (opts)=>{
            if (opts === null || opts === void 0 ? void 0 : opts.revert) {
                dispatchAction({
                    type: 'revert'
                });
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.checkboxGroup,
            children: [
                !hideSelectAll && /*#__PURE__*/ _jsx(CheckboxWithLabel, {
                    id: "all",
                    label: selectAllLabel,
                    sublabel: allCount ? "(".concat(allCount, ")") : null,
                    testId: "".concat(testId, "-item-all"),
                    checked: state.selectAll,
                    ellipsis: ellipsis,
                    onChange: ()=>dispatchAction({
                            type: 'selectAll'
                        })
                }),
                options.map((option, index)=>(!trimmedLowercaseSearch || option.label.toLowerCase().includes(trimmedLowercaseSearch)) && /*#__PURE__*/ _jsx(CheckboxWithLabel, {
                        id: option.value,
                        testId: "".concat(testId, "-item-").concat(option.value),
                        label: option.label,
                        sublabel: option.counter != null ? "(".concat(option.counter, ")") : null,
                        checked: state.selectedOptionsIndexes.has(index),
                        ellipsis: ellipsis,
                        onChange: ()=>{
                            if (bi === null || bi === void 0 ? void 0 : bi.name) {
                                const wasChecked = state.selectedOptionsIndexes.has(index);
                                logBiAction({
                                    entityType: 'multi_select_filter_picker',
                                    entityName: bi.name,
                                    actionType: wasChecked ? 'off' : 'on',
                                    data: _object_spread_props(_object_spread({}, bi.data), {
                                        test_id: testId !== null && testId !== void 0 ? testId : '',
                                        selected_value: option.label
                                    })
                                });
                            }
                            dispatchAction({
                                type: 'selectOption',
                                value: index
                            });
                        }
                    }, option.value))
            ]
        })
    });
}
